// Users screen: a searchable, paginated list of the active org's users. Each row
// shows the user's name, email, org and a summary of their assigned roles, and
// links to the user-detail view where roles are managed.
//
// Pagination + search are SERVER-SIDE: the page fetches one window at a time from
// GET /admin/api/users-with-roles, which already includes each user's roles
// summary — so there is no client-side slicing and no N per-user role calls. The
// search box is debounced so keystrokes don't spray requests at the backend.

import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { adminApi } from "../api/endpoints.js"
import type { UserRoleSummaryDto, UserTeamSummaryDto, UsersPageDto } from "../api/types.js"
import { fr } from "../i18n/fr.js"
import { useAsync } from "../lib/useAsync.js"
import { useDebouncedValue } from "../lib/useDebouncedValue.js"
import { userFullName } from "../lib/users.js"
import { useOrg } from "../state/org.js"
import { Alert, Button, Chip, Spinner } from "../ui/primitives.js"

const PAGE_SIZE = 12
const ROLE_CHIP_LIMIT = 3
const TEAM_CHIP_LIMIT = 3
const SEARCH_DEBOUNCE_MS = 300

export function UsersPage() {
  const navigate = useNavigate()
  const { activeOrg, orgs } = useOrg()

  const [query, setQuery] = useState("")
  const [page, setPage] = useState(1)
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS)

  // A new org, or a new (settled) search, resets the view to the first page.
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeOrg/debouncedQuery are reset triggers
  useEffect(() => {
    setPage(1)
  }, [activeOrg, debouncedQuery])

  // A new org also clears the search box. Separate from the page reset above so a
  // plain search change doesn't wipe the query the user just typed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeOrg is the reset trigger
  useEffect(() => {
    setQuery("")
  }, [activeOrg])

  // The server-side page window. Keyed on org + page + the DEBOUNCED query so the
  // backend does the filtering/slicing and the roles summary comes back inline.
  const result = useAsync<UsersPageDto | null>(
    () =>
      activeOrg
        ? adminApi.listUsersWithRoles({
            org: activeOrg,
            page,
            pageSize: PAGE_SIZE,
            q: debouncedQuery
          })
        : Promise.resolve(null),
    [activeOrg, page, debouncedQuery]
  )

  const orgName = useMemo(() => orgs.find((o) => o.id === activeOrg)?.name ?? "", [orgs, activeOrg])

  const data = result.data
  const users = data?.users ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1
  const currentPage = data?.page ?? page
  const hasSearch = debouncedQuery.trim().length > 0
  // First load has no data yet; later page/search fetches keep the old table on
  // screen (useAsync retains `data`), so only gate the initial spinner on `!data`.
  const firstLoad = result.loading && !data

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{fr.users.title}</h1>
          <p>{fr.users.description}</p>
        </div>
      </div>

      {!activeOrg ? <Alert>{fr.users.noOrg}</Alert> : null}

      {activeOrg ? (
        <>
          <div className="toolbar">
            <div style={{ minWidth: 280, flex: 1, maxWidth: 420 }}>
              <input
                type="search"
                className="field__input"
                aria-label={fr.users.searchLabel}
                placeholder={fr.users.searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            {data && !firstLoad ? (
              <span className="muted" style={{ fontSize: 13 }}>
                {fr.users.resultCount(total)}
              </span>
            ) : null}
          </div>

          {firstLoad ? <Spinner /> : null}
          {result.error ? <Alert variant="error">{fr.users.error}</Alert> : null}

          {data && !firstLoad ? (
            total === 0 ? (
              <Alert>{hasSearch ? fr.users.noMatch : fr.users.empty}</Alert>
            ) : (
              <>
                <div className="table-wrap">
                  <table className="table table--users">
                    <thead>
                      <tr>
                        <th>{fr.users.columns.name}</th>
                        <th>{fr.users.columns.email}</th>
                        <th>{fr.users.columns.org}</th>
                        <th>{fr.users.columns.roles}</th>
                        <th>{fr.users.columns.teams}</th>
                        <th className="table__actions">{fr.users.columns.actions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr
                          key={user.id}
                          data-clickable="true"
                          tabIndex={0}
                          onClick={() => navigate(`/users/${encodeURIComponent(user.id)}`)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              navigate(`/users/${encodeURIComponent(user.id)}`)
                            }
                          }}
                        >
                          <td>{userFullName(user)}</td>
                          <td className="mono">{user.email}</td>
                          <td>{orgName}</td>
                          <td>
                            <RolesSummary roles={user.roles} />
                          </td>
                          <td>
                            <TeamsSummary teams={user.teams} />
                          </td>
                          <td className="table__actions">
                            <div className="table__actions-row">
                              <Button
                                small
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/users/${encodeURIComponent(user.id)}`)
                                }}
                              >
                                {fr.users.manage}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 ? (
                  <div className="toolbar" style={{ justifyContent: "flex-end", marginTop: 16 }}>
                    <Button
                      small
                      variant="ghost"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      {fr.users.pagination.previous}
                    </Button>
                    <span className="muted" style={{ fontSize: 13 }}>
                      {fr.users.pagination.page(currentPage, totalPages)}
                    </span>
                    <Button
                      small
                      variant="ghost"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      {fr.users.pagination.next}
                    </Button>
                  </div>
                ) : null}
              </>
            )
          ) : null}
        </>
      ) : null}
    </div>
  )
}

/** Compact chips for a user's assigned roles, capped with a "+N" overflow chip. */
function RolesSummary({ roles }: { roles: UserRoleSummaryDto[] }) {
  if (roles.length === 0) {
    return <span className="muted">{fr.users.rolesEmpty}</span>
  }
  const shown = roles.slice(0, ROLE_CHIP_LIMIT)
  const overflow = roles.length - shown.length
  return (
    <div className="row" style={{ gap: 4 }}>
      {shown.map((role) => (
        <Chip key={role.id} system={role.isSystem}>
          {role.displayName || role.name}
        </Chip>
      ))}
      {overflow > 0 ? <Chip>{fr.users.rolesMore(overflow)}</Chip> : null}
    </div>
  )
}

/** Compact chips for a user's teams, capped with a "+N" overflow chip — the
 *  user-side mirror of the team's member list, styled like {@link RolesSummary}. */
function TeamsSummary({ teams }: { teams: UserTeamSummaryDto[] }) {
  if (teams.length === 0) {
    return <span className="muted">{fr.users.teamsEmpty}</span>
  }
  const shown = teams.slice(0, TEAM_CHIP_LIMIT)
  const overflow = teams.length - shown.length
  return (
    <div className="row" style={{ gap: 4 }}>
      {shown.map((team) => (
        <Chip key={team.id}>{team.name}</Chip>
      ))}
      {overflow > 0 ? <Chip>{fr.users.teamsMore(overflow)}</Chip> : null}
    </div>
  )
}
